import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Card,
  DatePicker,
  EmptyState,
  ErrorBoundary,
  Flex,
  Label,
  LoadingIndicator,
  Select,
  Text,
  toast,
} from "@jsq/ds/core";
import { useCoverageData } from "#hooks/useCoverageData";
import { useRegisterCoverageRefresh } from "#hooks/useCoverageRefresh";
import { useFayAuthGateArmed } from "#hooks/useFayAuthGate";
import { usePackageRoute } from "#hooks/usePackageRoute";
import { dateToIso, getCoverageStatusOptions, isoToDate } from "#utils/coverageGrid.utils";
import { isFayAuthFailure } from "#utils/fayAuthErrors";
import { CoverageGrid } from "../CoverageGrid";
import { PackageDetailSlideout } from "../PackageDetailSlideout";
import * as styles from "./styles_vanilla.css";
import { isString, isValidStatus, useUrlFilters } from "./urlFilters";

const CARD_TITLE = "Deliverable packages";

/** Fallback for a `CoverageGrid` render crash — e.g. a `CoverageCellState` this
 *  build's `getCellPresentation`/`getCoverageStatusOptions` don't recognize yet
 *  (a schema-expansion / rollout-ordering gap; see coverageGrid.utils' fallback
 *  presentation for the direct mitigation). This is the backstop for every
 *  *other* way that render can throw, not a substitute for handling known
 *  states gracefully — the filter row above stays interactive so a reviewer
 *  can retry with a narrower filter instead of losing the whole page. */
const GRID_CRASH_FALLBACK = (
  <EmptyState
    illustration="internal-error"
    title="Couldn't render the coverage grid"
    description="Try adjusting the filters or refreshing the page. If this keeps happening, contact support."
  />
);

export type CoverageViewProps = {
  /** Arena the coverage matrix and the package detail are scoped to. Undefined
   *  until the host app resolves it, which skips the queries — the package
   *  never reads a host app's arena context. */
  arenaId: number | undefined;
  /** Path the host app serves Fay at, which every package deep-link is built
   *  under. Defaults to `FAY_ROUTE_BASE`; a host that mounts Fay deeper passes
   *  its own prefix — Canopy passes `<base>/arena/<arenaId>`, so opening a
   *  package keeps the arena in the URL instead of navigating out of it.
   *
   *  Passing this obliges the host to also register a route matching
   *  `packageRoutePattern(routeBase)` — see that helper. Opening a package
   *  works without it, but a reload or pasted link lands on an unserved path. */
  routeBase?: string;
};

export const CoverageView: React.FC<CoverageViewProps> = ({ arenaId, routeBase }) => {
  // Fay MVP August (XFRG-941) release gate — see coverageGrid.utils'
  // getCoverageStatusOptions for the off-path (pre-XFRG-1190 3-state set,
  // legacy labels, no Confirmed issues filter option). Read through the
  // auth-gate context (not a direct flag read) so rendering outside a provider
  // doesn't drag a FlagProvider requirement into a host app or a test.
  const fayMVPAugust = useFayAuthGateArmed();
  const statusOptions = useMemo(() => getCoverageStatusOptions(fayMVPAugust), [fayMVPAugust]);
  const validStatuses = useMemo(
    () => new Set(statusOptions.map((o) => o.value)),
    [statusOptions],
  );

  // Every filter is URL-persisted (seeded on mount, written back on each edit)
  // so a refresh or a shared link reproduces the reviewer's picked window; the
  // router coupling lives in `urlFilters`, not here.
  const {
    filters: { dateRange, funds, statuses },
    setFilters,
  } = useUrlFilters(validStatuses);

  // The open package and its tab live in the URL (XFRG-1222) rather than local
  // state, so a reviewer can share the exact view they're looking at. Every
  // navigation below preserves `search`, so the filter params above survive
  // opening, switching tabs on, and closing a package.
  const { packageId, tab, openPackage, setTab, closePackage } = usePackageRoute(routeBase);

  // Fay returns the same empty result for an unknown id, a package in another
  // arena, and a malformed uuid, so the copy can't claim which one it was.
  const handlePackageNotFound = useCallback(() => {
    toast.error(
      "This package isn’t available. It may have been removed, or you may not have access.",
    );
    closePackage();
  }, [closePackage]);

  // All query orchestration lives in the hook; this component is just the
  // filter row + grid. The hook never reads a host arena context — pass the
  // resolved arenaId in; it skips the queries until the arena resolves.
  const { fundOptions, rows, periods, loading, error, refreshCoverage } = useCoverageData(
    {
      dateRange,
      funds,
      statuses,
    },
    arenaId,
  );

  // Publish `refreshCoverage` to the cross-subtree seam so the sibling
  // UploadRail can trigger a grid refresh (e.g. after classification approve)
  // without a shared parent state (XFRG-1129).
  const registerRefresh = useRegisterCoverageRefresh();
  useEffect(() => {
    registerRefresh(refreshCoverage);
    return () => registerRefresh(null);
  }, [registerRefresh, refreshCoverage]);

  // Reflect triage done in the slideout immediately instead of waiting for the
  // next poll tick. Keyed off the package closing rather than the drawer's
  // `onClose`, which only fires on an explicit dismissal — now that the URL
  // owns the selection, a package also closes via browser back and via the
  // not-found redirect.
  const openPackageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openPackageIdRef.current && !packageId) {
      refreshCoverage();
    }
    openPackageIdRef.current = packageId;
  }, [packageId, refreshCoverage]);

  // Only defer to the page gate when it's actually armed (provider mounted and
  // the XFRG-941 flag on); otherwise the inline error below stays the only
  // surface, as it was before XFRG-1212. Suppressing it while nothing will swap
  // the page would leave a spinner that never resolves.
  const gatedAuthFailure = fayMVPAugust && !!error && isFayAuthFailure(error);

  let content: React.ReactNode;
  // Spinner before the first response, and while a terminal auth failure is
  // in flight to the page gate (avoids one paint of a stale/empty grid before
  // the parent swaps to ErrorPage). A background refetch keeps the grid.
  if ((loading && rows.length === 0) || gatedAuthFailure) {
    content = (
      <Flex justifyContent="center" alignItems="center" className={styles.statusRail}>
        <LoadingIndicator />
      </Flex>
    );
  } else if (error) {
    // Transient/non-auth errors (e.g. a 500 or network blip) leave the page
    // usable — show the inline line. Auth failures are handled above.
    content = (
      <Flex justifyContent="center" alignItems="center" className={styles.statusRail}>
        <Text variant="regular-sm">Failed to load coverage: {error.message}</Text>
      </Flex>
    );
  } else {
    // ErrorBoundary here (not inside CoverageGrid itself) so a crash degrades
    // to a friendly empty state instead of white-screening, while the filter
    // row above stays mounted and interactive.
    content = (
      <ErrorBoundary
        fallback={GRID_CRASH_FALLBACK}
        additionalTags={{ feature: "fay-coverage-grid" }}
      >
        <CoverageGrid periods={periods} rows={rows} onPackageSelected={openPackage} />
      </ErrorBoundary>
    );
  }

  return (
    <Card isFullWidth padding="md" title={CARD_TITLE}>
      <Flex flexDirection="column" gap={16}>
        <Flex gap={10} alignItems="flex-end">
          <Flex flexDirection="column" className={styles.filterColumn}>
            <Select
              multiple
              label="Fund"
              placeholder="All funds"
              value={funds}
              onChange={(val) =>
                setFilters((f) => ({
                  ...f,
                  funds: (Array.isArray(val) ? val : []).filter(isString),
                }))
              }
              full
            >
              <Select.Section name="Funds">
                {fundOptions.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          </Flex>
          <Flex flexDirection="column" className={styles.filterColumn}>
            <Select
              multiple
              label="Status"
              placeholder="All statuses"
              value={statuses}
              onChange={(val) =>
                setFilters((f) => ({
                  ...f,
                  // Same guard as the URL parser, so a stale option cannot feed
                  // the query a flag-gated state.
                  statuses: (Array.isArray(val) ? val : []).filter(
                    isValidStatus(validStatuses),
                  ),
                }))
              }
              full
            >
              <Select.Section name="Statuses">
                {statusOptions.map((o) => (
                  <Select.Option key={o.value} value={o.value}>
                    {o.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          </Flex>
          <Flex flexDirection="column" className={styles.filterColumn}>
            <Label>Period start date</Label>
            <DatePicker
              hasIcon
              isFull
              selected={isoToDate(dateRange.start)}
              onChange={(d: Date | null) =>
                d &&
                setFilters((f) => ({
                  ...f,
                  dateRange: { ...f.dateRange, start: dateToIso(d) },
                }))
              }
              dateFormat="MMM d, yyyy"
            />
          </Flex>
          <Flex flexDirection="column" className={styles.filterColumn}>
            <Label>Period end date</Label>
            <DatePicker
              hasIcon
              isFull
              selected={isoToDate(dateRange.end)}
              minDate={isoToDate(dateRange.start)}
              onChange={(d: Date | null) =>
                d &&
                setFilters((f) => ({ ...f, dateRange: { ...f.dateRange, end: dateToIso(d) } }))
              }
              dateFormat="MMM d, yyyy"
            />
          </Flex>
        </Flex>

        {content}
      </Flex>
      <PackageDetailSlideout
        packageId={packageId}
        activeTab={tab}
        onTabChange={setTab}
        onClose={closePackage}
        onNotFound={handlePackageNotFound}
        arenaId={arenaId}
      />
    </Card>
  );
};
